import styled from 'styled-components'

import { COLORS } from '../../commons/Colors'
import LoadingComponent from '../../commons/components/LoadingComponent'
import MainHeaderComponent from '../../commons/components/MainHeaderComponent'
import RoundButtonComponent from '../../commons/components/RoundButtonComponent'
import SnackBarComponent from '../../commons/components/SnackBarComponent'
import { SnackbarConfigTypes } from '../../commons/utils/Types'
import { Client } from '../../models/Client'
import GlobalThemeProvider from '../../style/GlobalTheme'
import { formatDocument } from '../../utils/Utils'

type ClientSearchViewProps = {
    isLoading: boolean
    legacyCode: string
    client: Client | null
    snackbarConfig: SnackbarConfigTypes
    handleCloseSnackbar: () => void
    handleLegacyCodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleSearch: () => void
}

function ClientSearchView({
    isLoading,
    legacyCode,
    client,
    snackbarConfig,
    handleCloseSnackbar,
    handleLegacyCodeChange,
    handleSearch,
}: ClientSearchViewProps): JSX.Element {
    return (
        <GlobalThemeProvider>
            <PageContainer>
                <MainHeaderComponent />
                <PageTitle>Busca de Cliente</PageTitle>
                {isLoading ? (
                    <LoadingComponent />
                ) : (
                    <MainContainer>
                        <SubTitle>BUSCAR CLIENTE POR CÓDIGO</SubTitle>
                        <SearchContainer>
                            <InputContainer>
                                <LabelField htmlFor="legacy-code-input">
                                    Código do cliente
                                </LabelField>
                                <InputField
                                    id="legacy-code-input"
                                    placeholder="Digite o código do cliente"
                                    value={legacyCode}
                                    onChange={handleLegacyCodeChange}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch()
                                        }
                                    }}
                                />
                            </InputContainer>
                            <ButtonContainer>
                                <RoundButtonComponent
                                    text="Buscar"
                                    size="large"
                                    onClick={handleSearch}
                                />
                            </ButtonContainer>
                        </SearchContainer>
                        {client && (
                            <ClientInfoContainer>
                                <ClientInfoTitle>
                                    Informações do Cliente
                                </ClientInfoTitle>
                                <ClientInfoGrid>
                                    <ClientInfoItem>
                                        <InfoLabel>Código:</InfoLabel>
                                        <InfoValue>
                                            {client.legacyCode}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Nome Fantasia:</InfoLabel>
                                        <InfoValue>
                                            {client.tradeName}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Razão Social:</InfoLabel>
                                        <InfoValue>
                                            {client.companyName}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Documento:</InfoLabel>
                                        <InfoValue>
                                            {formatDocument(client.document)}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Email:</InfoLabel>
                                        <InfoValue>
                                            {client.email || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>
                                            Inscrição Estadual:
                                        </InfoLabel>
                                        <InfoValue>
                                            {client.stateInscription || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Endereço:</InfoLabel>
                                        <InfoValue>
                                            {client.address || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>
                                            Endereço de Cobrança:
                                        </InfoLabel>
                                        <InfoValue>
                                            {client.billingAddress || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Cidade:</InfoLabel>
                                        <InfoValue>
                                            {client.city || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Estado:</InfoLabel>
                                        <InfoValue>
                                            {client.state || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>País:</InfoLabel>
                                        <InfoValue>
                                            {client.country || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Indústria:</InfoLabel>
                                        <InfoValue>
                                            {client.industry || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Segmento:</InfoLabel>
                                        <InfoValue>
                                            {client.deliveryPlant === '1352'
                                                ? 'Mycofix'
                                                : 'Ruminant'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>
                                            Escritório de Vendas:
                                        </InfoLabel>
                                        <InfoValue>
                                            {client.salesOffice?.name || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>Grupo de Vendas:</InfoLabel>
                                        <InfoValue>
                                            {client.salesGroup?.name || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>
                                            Distrito de Vendas:
                                        </InfoLabel>
                                        <InfoValue>
                                            {client.salesDistrict?.name || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    <ClientInfoItem>
                                        <InfoLabel>
                                            Zona de Transporte:
                                        </InfoLabel>
                                        <InfoValue>
                                            {client.transportationZone || '-'}
                                        </InfoValue>
                                    </ClientInfoItem>
                                    {client.owner && (
                                        <>
                                            <ClientInfoItem>
                                                <InfoLabel>
                                                    Nome do Proprietário:
                                                </InfoLabel>
                                                <InfoValue>
                                                    {client.owner.name || '-'}
                                                </InfoValue>
                                            </ClientInfoItem>
                                            <ClientInfoItem>
                                                <InfoLabel>
                                                    Telefone do Proprietário:
                                                </InfoLabel>
                                                <InfoValue>
                                                    {client.owner.phone || '-'}
                                                </InfoValue>
                                            </ClientInfoItem>
                                            <ClientInfoItem>
                                                <InfoLabel>
                                                    Telefone Fixo do
                                                    Proprietário:
                                                </InfoLabel>
                                                <InfoValue>
                                                    {client.owner.landline ||
                                                        '-'}
                                                </InfoValue>
                                            </ClientInfoItem>
                                            <ClientInfoItem>
                                                <InfoLabel>
                                                    Última atualização:
                                                </InfoLabel>
                                                <InfoValue>
                                                    {new Date(
                                                        client.updatedAt
                                                    ).toLocaleString('pt-BR')}
                                                </InfoValue>
                                            </ClientInfoItem>
                                        </>
                                    )}
                                </ClientInfoGrid>
                            </ClientInfoContainer>
                        )}
                    </MainContainer>
                )}
            </PageContainer>
            <SnackBarComponent
                message={snackbarConfig.message}
                isVisible={snackbarConfig.isVisible}
                severity={snackbarConfig.status}
                handleClose={handleCloseSnackbar}
            />
        </GlobalThemeProvider>
    )
}

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background-color: ${COLORS.secondaryWhite};
`

const PageTitle = styled.h1`
    color: ${COLORS.primaryOrange};
    font-size: 18px;
    font-family: Roboto;
    margin: auto;
    font-weight: normal;
    margin-top: 25px;
`

const MainContainer = styled.div`
    background-color: ${COLORS.primaryWhite};
    border: solid 1px ${COLORS.borderGray};
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    margin: 30px;
    padding: 25px 25px 50px 25px;
`

const SubTitle = styled.h1`
    align: start;
    color: ${COLORS.primaryGray};
    font-size: 12px;
    font-family: Roboto;
    font-weight: bold;
    text-align: start;
    margin-bottom: 40px;
`

const SearchContainer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 20px;
`

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 30%;
`

const LabelField = styled.label`
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    font-size: 12px;
    font-weight: bold;
    line-height: 2;
    margin-bottom: 5px;
    text-transform: uppercase;
`

const InputField = styled.input`
    border: 1px solid ${COLORS.disabledButton};
    border-radius: 4px;
    font-family: Roboto;
    font-size: 14px;
    height: 30px;
    line-height: 1.43;
    padding: 8px;

    :focus {
        outline-color: ${COLORS.primaryOrange};
    }
`

const ButtonContainer = styled.div`
    max-height: 40px;
`

const ClientInfoContainer = styled.div`
    margin-top: 40px;
    padding-top: 40px;
    border-top: 1px solid ${COLORS.borderGray};
`

const ClientInfoTitle = styled.h2`
    color: ${COLORS.primaryGray};
    font-size: 16px;
    font-family: Roboto;
    font-weight: bold;
    margin-bottom: 20px;
`

const ClientInfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px 30px;
`

const ClientInfoItem = styled.div`
    display: flex;
    flex-direction: column;
`

const InfoLabel = styled.span`
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 5px;
`

const InfoValue = styled.span`
    color: ${COLORS.primaryGray};
    font-family: Roboto;
    font-size: 14px;
    line-height: 1.43;
`

export default ClientSearchView
